import React from "react";
import { addDays } from "date-fns";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { isArray, isObject } from "lodash";
import { toast } from "react-semantic-toasts";
import { DateInput } from "semantic-ui-calendar-react";
import { Form, Button, Input } from "formik-semantic-ui";
import { Segment, Message, Header } from "semantic-ui-react";

import { validateStampRollUpdate, validateStampRollCreate } from "utilities/dist/stamp/validator";
import { saveStamp, setStampEditorError, setStampEditorInput } from "../actions";
import { DateFormat } from "utilities/dist/invoice/constants";

const MAX_STAMP_COUNT_THRESHOLD = 10000;
const MIN_STAMP_COUNT_THRESHOLD = 80;

const StampCountWarning = ({ from = 0, to = 0 }) => {
    let totalStampCount = 0;

    if (to && from) {
        totalStampCount = parseInt(to) - parseInt(from) + 1;
    }

    if (!totalStampCount) return null;

    if (totalStampCount > MAX_STAMP_COUNT_THRESHOLD) {
        return (
            <Message
                icon="warning sign"
                content={`You are adding ${totalStampCount} stamps which is more than the max possible number of stamps in a roll, please make sure you've entered the digits correctly`}
            />
        );
    }

    if (totalStampCount < MIN_STAMP_COUNT_THRESHOLD) {
        return (
            <Message
                icon="warning sign"
                content={`Stamp rolls usually contain at least 80 stamps, you're trying to add a roll with only ${totalStampCount} stamps, please make sure you've entered the digits correctly`}
            />
        );
    }

    if (totalStampCount % 80 !== 0 && totalStampCount % 500 !== 0) {
        return (
            <Message
                icon="warning sign"
                content={`Stamp rolls usually contain 80/500 stamps per roll, you're trying to add a roll with ${totalStampCount} stamps, please make sure you've entered the digits correctly`}
            />
        );
    }

    return null;
};

class StampRollContainerEditor extends React.Component {
    handleSubmit = async (stampData, formik) => {
        try {
            if (stampData.id) {
                stampData = await validateStampRollUpdate(stampData, {});
            } else {
                stampData = await validateStampRollCreate(stampData, {});
            }

            const savedStamp = await this.props.saveStamp(stampData);
            formik.setSubmitting(false);
            if (savedStamp) {
                formik.resetForm();
            }
        } catch (err) {
            const messages = [];

            if (err.details) {
                err.details.forEach(errDet => {
                    formik.setFieldError(errDet.context.key, errDet.message);
                    messages.push(errDet.message);
                });
            }

            formik.setSubmitting(false);
            this.props.setStampEditorError(messages);
        }
    };

    // TODO: Tis is not actually used so can probably be removed but needs a double check
    // this pattern is used when there's a confirmation stage before actually committing the requested action
    handleConfirm = data => {
        this.formikApi && this.formikApi.setSubmitting(false);

        if (data === null) {
            return;
        }

        if (isArray(data)) {
            data.forEach(errDet => {
                this.formikApi.setFieldError(errDet.context.key, errDet.message);
            });
            return;
        }

        if (isObject(data)) {
            let description = ["Stamp saved in database."];

            toast({
                description: <Message.List items={description} />,
                time: 6000,
                type: "success",
                icon: "checkmark",
                title: "Created Stamp",
            });
        }
    };

    selectEmptyNumber = e => {
        if (!e.target.value || e.target.value === "0") {
            e.target.select();
        }
    };

    render() {
        const { inputErrors, inputs, isSaving } = this.props;
        return (
            <>
                <Segment clearing attached="top">
                    <Header
                        subheader="Got a new stamp roll, register it here to keep track of them"
                        content="Add Stamp Roll"
                        as="h3"
                        floated="left"
                    />
                    <Header floated="right">
                        <Button
                            as="a"
                            href={"https://totalwine.atlassian.net/servicedesk/customer/portals"}
                            color="blue"
                            target="_blank"
                            icon="external"
                            content="Buy Stamps"
                            data-cy="buy_stamps_button"
                        />
                    </Header>
                </Segment>

                <Segment attached loading={isSaving}>
                    <Form
                        initialValues={inputs}
                        isSubmitting={isSaving}
                        onSubmit={this.handleSubmit}
                        render={({ values, setFieldValue }) => {
                            return (
                                <>
                                    <Form.Children>
                                        <Form.Group widths="2">
                                            <Input
                                                name="from"
                                                label="Start"
                                                inputProps={{
                                                    type: "number",
                                                    "data-cy": "stamp_from",
                                                    onFocus: this.selectEmptyNumber,
                                                }}
                                            />

                                            <Input
                                                name="to"
                                                label="End"
                                                inputProps={{
                                                    type: "number",
                                                    "data-cy": "stamp_to",
                                                    onFocus: this.selectEmptyNumber,
                                                }}
                                            />
                                        </Form.Group>

                                        <Form.Group widths={2}>
                                            <Input
                                                name="prefix"
                                                label="Prefix"
                                                inputProps={{
                                                    "data-cy": "stamp_prefix",
                                                }}
                                            />

                                            <DateInput
                                                animation="none"
                                                name="received_at"
                                                placeholder="Date"
                                                iconPosition="left"
                                                label="Received On"
                                                closeOnMouseLeave={true}
                                                value={values.received_at}
                                                maxDate={addDays(new Date(), 3)}
                                                dateFormat={DateFormat.toUpperCase()}
                                                onChange={(e, data) =>
                                                    setFieldValue("received_at", data.value)
                                                }
                                            />
                                        </Form.Group>

                                        <Button.Submit data-cy="save_roll" positive>
                                            Save Roll
                                        </Button.Submit>
                                    </Form.Children>

                                    <StampCountWarning {...values} />
                                </>
                            );
                        }}
                    />

                    {inputErrors.length > 0 && (
                        <Message
                            error
                            icon="warning sign"
                            content={inputErrors}
                            header="Error saving stamp!"
                        />
                    )}
                </Segment>
            </>
        );
    }
}

const mapStateToProps = ({ stampRoll }) => ({
    inputs: stampRoll.inputs,
    isSaving: stampRoll.isSaving,
    inputErrors: stampRoll.inputErrors,
});

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            saveStamp,
            setStampEditorError,
            setStampEditorInput,
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(StampRollContainerEditor);
